import { createElement, useMemo } from 'react';

import type {
  A11y,
  Alignment,
  Color,
  IceFunction,
  TypoFamily,
  TypoLineHeight,
  TypoSize,
  TypoTag,
  TypoType,
  TypoWeight,
} from '@snowit/icetypes';

import { TYPO_SIZE_SCALE } from '@snowit/iceconst';
import { COLOR } from '@snowit/icetypes';
import testId from '@snowit/utils/testId';

import cx from 'classnames';
import s from './Typo.module.scss';

export interface ITypo {
  tag?: TypoTag;
  family?: TypoFamily;
  size?: TypoSize | [TypoSize, TypoSize];
  sizeIncrease?: number;
  weight?: TypoWeight;
  lineHeight?: TypoLineHeight;
  text: string | number;
  color?: Color | (string & {});
  align?: Alignment;
  decoration?: 'underline' | 'none';
  balancer?: boolean;
  ellipsis?: boolean;
  onClick?: IceFunction;
  className?: string;
  id?: string;
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
  /** @deprecated The props should not be used, use size + weight instead */
  type?: TypoType;
}

/**
 Typo is a utility component for rendering an already stylized text.
 */
const Typo = (props: ITypo) => {
  const {
    text,
    tag = 'span',
    family = 'default',
    size,
    sizeIncrease = 2,
    weight,
    lineHeight = 'default',
    align,
    decoration = 'none',
    balancer = false,
    ellipsis,
    onClick,
    className,
    id,
    a11y,
    test,
    // @deprecated
    type: _type = 'tDF',
  } = props;

  const [sizeMin, sizeMax] = Array.isArray(size) ? size : [size, size];

  const type = useMemo(() => {
    if (_type.startsWith('h')) {
      return {
        size: _type.slice(1).toLowerCase(),
        weight: 'semi-bold',
        color: 'var(--c-text)',
      };
    }

    return {
      size: _type.slice(1).toLowerCase(),
      weight: 'regular',
      color: 'var(--c-text-l)',
    };
  }, [_type]);

  const color = useMemo(() => {
    return COLOR.includes(props.color as Color) ? `var(--c-${props.color})` : props.color;
  }, [props.color]);

  const typoProps = useMemo(() => {
    return {
      className: cx(s.typo, className, {
        [s.balancer]: balancer,
        [s.ellipsis]: ellipsis,
      }),
      id,
      style: {
        '--lc': color || type.color,
        '--lff': family === 'default' ? 'var(--ff)' : 'var(--ff-mono)',
        '--lfw': `var(--fw-${weight || type.weight})`,
        '--lfs-min': `var(--fs-${sizeMin || type.size})`,
        '--lfs-max': `var(--fs-${sizeMax || type.size})`,
        '--lfs-increase': `${sizeIncrease}vw`,
        '--lfs-relative': `${TYPO_SIZE_SCALE[sizeMin || type.size] / TYPO_SIZE_SCALE[sizeMax || type.size]}rem`,
        '--llh': lineHeight === 'default' ? '0.25rem' : '0.5em',
        '--lalign': align,
        '--ldecoration': decoration,
      },
      onClick,
      role: a11y?.role,
      htmlFor: a11y?.htmlFor,
      ...testId(test),
    };
  }, [type, family, size, weight, lineHeight, color, ellipsis, className]);

  return createElement(tag, typoProps, text);
};

export default Typo;
